import React, { useRef, useCallback, useMemo } from 'react';
import { KiteCard, KiteAlert, KiteIcon } from '@kite/react-kite';
import { ITCOForm, ITCOSubmission } from 'types';
import { useTCORenderFields } from 'hooks';
import { tcoFieldsIds } from 'utils/defaultsAndConstants/constants';
import SpectrumDiyTotal from 'components/TCOComponents/TCOOverview/SpectrumDiyTotal';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';
import './TCOTravelLabor.scss';

export interface ITCOTravelLaborProps {
  form: ITCOForm;
  tcoSubmissions: ITCOSubmission[];
}

const TCOImplementationCostsTravelLabor = ({
  form,
  tcoSubmissions,
}: ITCOTravelLaborProps) => {
  const { name, fields, id } = form;
  const ref = useRef<HTMLFormElement>(null);

  const { fieldComponents, fieldValues, updateTcoSubmissionsError } =
    useTCORenderFields({
      fields,
      tcoSubmissions,
      formId: id,
    });

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  }, []);

  const {
    spectrumTotalTravelCost,
    diyTotalTravelCost,
    spectrumTotalLaborCost,
    diyTotalLaborCost,
  } = useMemo(() => {
    const {
      numberOfSitesFieldId,
      laborCostsFieldId,
      laborHoursPerSiteFieldId,
      avgTravelCostPerSiteFieldId,
    } = tcoFieldsIds;

    const numberOfSites = parseInt(fieldValues[numberOfSitesFieldId] || '0');
    const laborCost = parseFloat(fieldValues[laborCostsFieldId] || '0');
    const laborHoursPerSite = parseInt(
      fieldValues[laborHoursPerSiteFieldId] || '0'
    );
    const avgTravelCostPerSite = parseFloat(
      fieldValues[avgTravelCostPerSiteFieldId] || '0'
    );

    return {
      spectrumTotalTravelCost: 0,
      spectrumTotalLaborCost: 0,
      diyTotalTravelCost: numberOfSites * avgTravelCostPerSite,
      diyTotalLaborCost: numberOfSites * laborHoursPerSite * laborCost,
    };
  }, [fieldValues]);

  return (
    <KiteCard className="tco-travel-labor">
      <h3 className="tco-travel-labor__title">{name}</h3>
      <div className="implementation-costs">
        {updateTcoSubmissionsError && (
          <KiteAlert
            type="alert"
            title="Save Error"
            description="Something went wrong saving your answers. Any changes will not be saved. Please refresh and try again later."
          />
        )}
        <form ref={ref} onSubmit={handleSubmit}>
          <div className="implementation-costs__number-sites">
            {fieldComponents.slice(0, 1)}
          </div>
          <div className="implementation-costs__travel-labor-container">
            <div className="implementation-costs__travel-labor-spectrum">
              <h4 className="implementation-costs__section-title">SPECTRUM</h4>
              <div className="implementation-costs__content">
                <div className="implementation-costs__included-item">
                  <KiteIcon
                    name="checkmark-circle-f"
                    color="green"
                    size="24px"
                  />
                  <span className="implementation-costs__included-text">
                    Labor Costs Included
                  </span>
                </div>
                <div className="implementation-costs__included-item">
                  <KiteIcon
                    name="checkmark-circle-f"
                    color="green"
                    size="24px"
                  />
                  <span className="implementation-costs__included-text">
                    Travel Costs Included
                  </span>
                </div>
              </div>
            </div>
            <div className="implementation-costs__vertical-divider"></div>
            <div className="implementation-costs__travel-labor-diy">
              <h4 className="implementation-costs__section-title">DIY</h4>
              <div className="implementation-costs__inputs">
                {fieldComponents.slice(1)}
              </div>
            </div>
          </div>
          <div className="implementation-costs__totals">
            <SpectrumDiyTotal
              title="Spectrum Total"
              primaryCostLabel="Total Travel Cost"
              primaryCost={`$${tcoFormattedPrice(spectrumTotalTravelCost)}`}
              secondaryCostLabel="Total Labor Cost"
              secondaryCost={`$${tcoFormattedPrice(spectrumTotalLaborCost)}`}
            />
            <SpectrumDiyTotal
              title="DIY Total"
              primaryCostLabel="Total Travel Cost"
              primaryCost={`$${tcoFormattedPrice(diyTotalTravelCost)}`}
              secondaryCostLabel="Total Labor Cost"
              secondaryCost={`$${tcoFormattedPrice(diyTotalLaborCost)}`}
              isDiy={true}
            />
          </div>
        </form>
      </div>
    </KiteCard>
  );
};

export default TCOImplementationCostsTravelLabor;
